import React, { useEffect, useRef } from 'react';

import Button from '@amzn/meridian/button';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import { useMutation, useQuery } from '@tanstack/react-query';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import useForm from 'src/hooks/useForm';
import { getSiteQueryOptions } from 'src/services/sites/siteQueries';
import { getTaskMutationOptions } from 'src/services/tasks/taskQueries';
import { type NonPhysicalTask, type NonPhysicalTaskDTO } from 'src/services/tasks/types';
import { AccommodationTypeIds } from 'src/types/accommodationTypes';

import {
  MAX_DESCRIPTION_CHAR_LENGTH,
  MAX_DESCRIPTION_ERR_MSG,
  MAX_NAME_CHAR_LENGTH,
  MAX_NAME_ERR_MSG,
} from '../constants';

import NonPhysicalTaskFormFields from './nonPhysicalTaskFormFields';
import { type NonPhysicalTaskTableRow } from './nonPhysicalTaskTable';

interface NonPhysicalTaskModalProps {
  selectedTask: NonPhysicalTaskTableRow | null;
  isOpen: boolean;
  onSave: (data: NonPhysicalTask) => void;
  onCancel: () => void;
}

const NonPhysicalTaskModal = ({ selectedTask, isOpen, onSave, onCancel }: NonPhysicalTaskModalProps) => {
  const { data: regions = [], isSuccess: isRegionsLoaded } = useQuery(getSiteQueryOptions.regions());
  const createNonPhysicalTask = useMutation(getTaskMutationOptions.nonPhysicalTasks.create());
  const updateNonPhysicalTask = useMutation(getTaskMutationOptions.nonPhysicalTasks.update());

  const { validateForm, handleChange, resetForm, setFormValues, resetFormField, formData, errors } =
    useForm<NonPhysicalTaskDTO>({
      validations: {
        name: {
          required: {
            value: true,
            message: 'Task Name is required',
          },
          custom: {
            isValid: (value) => value.length <= MAX_NAME_CHAR_LENGTH,
            message: MAX_NAME_ERR_MSG,
          },
        },
        regionId: { required: { value: true, message: 'Region is required' } },
        accommodationTypeId: { required: { value: true, message: 'Accommodation Type is required' } },
        description: {
          required: {
            value: false,
          },
          custom: {
            isValid: (value) => value.length <= MAX_DESCRIPTION_CHAR_LENGTH,
            message: MAX_DESCRIPTION_ERR_MSG,
          },
        },
      },
      initialValues: {
        name: '',
        description: '',
        allowsDuration: false,
        allowsFrequency: false,
        allowsFreeFormText: false,
      },
    });

  useEffect(() => {
    if (!isOpen || !isRegionsLoaded) return;
    if (selectedTask) {
      const region = regions.find((region) => region?.name === selectedTask.region);
      if (region) {
        setFormValues({
          id: selectedTask.id,
          name: selectedTask.name,
          accommodationTypeId: AccommodationTypeIds[selectedTask.accommodationType],
          description: selectedTask.description ?? '',
          regionId: region.id,
          allowsDuration: selectedTask.allowsDuration,
          allowsFrequency: selectedTask.allowsFrequency,
          allowsFreeFormText: selectedTask.allowsFreeFormText,
        });
        prevCountryRef.current = selectedTask.country;
      }
    } else {
      prevCountryRef.current = undefined;
      resetForm();
    }
  }, [isOpen, selectedTask, isRegionsLoaded]);

  const prevCountryRef = useRef<string | undefined>();
  useEffect(() => {
    const selectedCountry = regions.find((r) => r.id === formData.regionId)?.country?.name;
    if (!!selectedCountry && selectedCountry !== prevCountryRef?.current) {
      resetFormField('accommodationTypeId');
    }
    prevCountryRef.current = selectedCountry;
  }, [formData.regionId]);

  const save = () => {
    if (validateForm() && isRegionsLoaded) {
      const saveMutation = !formData.id ? createNonPhysicalTask : updateNonPhysicalTask;
      saveMutation.mutate(formData, {
        onSuccess: (task) => {
          onSave(task);
        },
      });
    }
  };

  const cancel = () => {
    onCancel();
    resetForm();
  };

  return (
    <Modal
      title={selectedTask ? 'Edit Non-Physical Task' : 'Add New Non-Physical Task'}
      open={isOpen}
      onClose={onCancel}
      closeLabel="Close"
      aria-describedby="modal-description"
      width="600px"
      scrollContainer="modal"
    >
      <LoadingSpinnerWrapper showSpinner={createNonPhysicalTask.isPending || updateNonPhysicalTask.isPending}>
        <NonPhysicalTaskFormFields formData={formData} errors={errors} handleChange={handleChange} />
      </LoadingSpinnerWrapper>

      <ModalFooter>
        <Row alignmentHorizontal="end">
          <Button type="secondary" size="medium" onClick={cancel}>
            Cancel
          </Button>
          <Button type="primary" size="medium" onClick={save}>
            Save
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default NonPhysicalTaskModal;
